import React, { useState, useEffect } from "react";
import {
    Dialog,
    IconButton,
    makeStyles,
    Button,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import PhoneMockup from "../../../images/banner-preview-phone.png";
import theme from "../../../themes";

const useStyles = makeStyles(() => ({
    dialogPaper: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: "20px",
        overflow: "visible",
        maxWidth: "none",
        padding: "20px 30px 30px 30px",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    title: {
        fontFamily: "DM Sans, sans-serif",
        fontSize: "24px",
        fontWeight: 700,
        color: theme.palette.primary.main,
        textAlign: "center",
        marginBottom: "16px",
    },
    closeButton: {
        position: "absolute",
        top: "12px",
        right: "12px",
        backgroundColor: theme.palette.delete.main,
        color: theme.palette.delete.contrastText,
        width: "30px",
        height: "30px",
        padding: "0",
        zIndex: 3,
        "&:hover": {
            backgroundColor: theme.palette.delete.dark,
        },
    },
    closeIcon: {
        fontSize: "16px",
    },
    wrapper: {
        position: "relative",
        display: "inline-block",
    },
    phoneImage: {
        width: "340px",
        height: "auto",
        display: "block",
    },
    // Banner overlay positioned over the white empty area in the phone mockup
    bannerOverlay: {
        position: "absolute",
        top: "11.5%",
        left: "8%",
        width: "84%",
        height: "26%",
        overflow: "hidden",
    },
    bannerImage: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
    },
    bannerVideo: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        border: "none",
    },
    // Navigation arrows over the banner
    navigationButton: {
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        backgroundColor: "rgba(0,0,0,0.35)",
        color: "white",
        "&:hover": {
            backgroundColor: "rgba(0,0,0,0.55)",
        },
        padding: "3px",
        minWidth: "unset",
        borderRadius: "50%",
        width: "24px",
        height: "24px",
        zIndex: 2,
    },
    // Dots indicator
    dotsContainer: {
        position: "absolute",
        bottom: "6px",
        left: "0",
        right: "0",
        display: "flex",
        justifyContent: "center",
        gap: "5px",
        zIndex: 2,
    },
    dot: {
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        backgroundColor: "rgba(255,255,255,0.5)",
        transition: "all 0.3s ease",
    },
    activeDot: {
        backgroundColor: theme.palette.background.paper,
    },
}));

interface BannerPreviewModalProps {
    open: boolean;
    onClose: () => void;
    banners: Array<{
        image: string | null;
        videoUrl: string | null;
        redirectUrl: string | null;
    }>;
    phoneMockupImage?: string;
}

const BannerPreviewModal: React.FC<BannerPreviewModalProps> = ({
    open,
    onClose,
    banners,
    phoneMockupImage,
}) => {
    const classes = useStyles();
    const [currentIndex, setCurrentIndex] = useState(0);

    const activeBanners = banners.filter(b => b.image || b.videoUrl);

    // Reset index when banners change to prevent out-of-bounds access
    useEffect(() => {
        if (currentIndex >= activeBanners.length) {
            setCurrentIndex(Math.max(0, activeBanners.length - 1));
        }
    }, [activeBanners.length, currentIndex]);

    const handleNext = () => {
        setCurrentIndex((prev) => (prev + 1) % activeBanners.length);
    };

    const handlePrev = () => {
        setCurrentIndex((prev) => (prev - 1 + activeBanners.length) % activeBanners.length);
    };

    if (activeBanners.length === 0) return null;

    const safeIndex = Math.min(currentIndex, activeBanners.length - 1);
    const currentBanner = activeBanners[safeIndex];

    return (
        <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }}>
            {/* Close button */}
            <IconButton className={classes.closeButton} onClick={onClose}>
                <CloseIcon className={classes.closeIcon} />
            </IconButton>

            <div className={classes.title}>Banner Preview</div>

            <div className={classes.wrapper}>
                <img
                    src={phoneMockupImage || PhoneMockup}
                    alt="Phone Preview"
                    className={classes.phoneImage}
                />

                {/* Banner overlay */}
                <div className={classes.bannerOverlay}>
                    {currentBanner.image ? (
                        <img
                            src={currentBanner.image}
                            alt={`Banner ${currentIndex + 1}`}
                            className={classes.bannerImage}
                        />
                    ) : currentBanner.videoUrl ? (
                        <iframe
                            src={(() => {
                                try {
                                    const url = currentBanner.videoUrl;
                                    // If already in embed format, return as is
                                    if (url.includes('/embed/')) return url;
                                    // Handle youtube.com/watch?v= format
                                    if (url.includes('watch?v=')) {
                                        return url.replace('watch?v=', 'embed/').split('&')[0];
                                    }
                                    // Handle youtu.be/ format
                                    if (url.includes('youtu.be/')) {
                                        const videoId = url.split('youtu.be/')[1]?.split('?')[0];
                                        return `https://www.youtube.com/embed/${videoId}`;
                                    }
                                    return url;
                                } catch (error) {
                                    console.error('Error processing video URL:', error);
                                    return currentBanner.videoUrl;
                                }
                            })()}
                            title={`Banner ${currentIndex + 1}`}
                            className={classes.bannerVideo}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            onError={(e) => console.error('Video load error:', e)}
                        />
                    ) : null}

                    {/* Navigation arrows */}
                    {activeBanners.length > 1 && (
                        <>
                            <Button
                                className={classes.navigationButton}
                                style={{ left: "4px" }}
                                onClick={handlePrev}
                            >
                                <ArrowBackIosIcon style={{ fontSize: "10px", marginLeft: "3px" }} />
                            </Button>
                            <Button
                                className={classes.navigationButton}
                                style={{ right: "4px" }}
                                onClick={handleNext}
                            >
                                <ArrowForwardIosIcon style={{ fontSize: "10px" }} />
                            </Button>
                        </>
                    )}

                    {/* Dots */}
                    {activeBanners.length > 1 && (
                        <div className={classes.dotsContainer}>
                            {activeBanners.map((_, idx) => (
                                <div
                                    key={idx}
                                    className={`${classes.dot} ${idx === currentIndex ? classes.activeDot : ""}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Dialog>
    );
};

export default BannerPreviewModal;
